/**
 * adaptReportForDraft — buildReport 출력(d) → DnaReportDraft가 기대하는 뷰모델(SAMPLE 형태)로 변환.
 *
 * DnaReportDraft(2026-06-24 재디자인)는 SAMPLE에 손으로 넣은 파생 필드
 *  (lastExam·funnel·typeSummary·byType·byBandMasteredTop·배열형 difficultyBands·
 *   weakRanked[].ladder/direct/related/st/topPairPartners(이름+개념타입) 등)를 기대하지만,
 * buildReport는 이를 만들지 않아 undefined.map 크래시가 난다. 이 어댑터가 그 갭을 채운다.
 * (어댑터는 buildReport 출력↔DnaReportDraft 뷰모델 사이의 StudentsHub 로컬 glue. common/report는
 *  KY Report 전용으로 함께 수정됨 — 현재 StudentsHub 외 소비처 없음(grep 확인).)
 *
 * selectedConceptIds: 처방(정밀진단 상위5·처방 상세·학습 계획)의 '우선 정렬' 기준(2026-07-04 정책 변경).
 *   종전 하드 필터(선택 밖 개념 제외) → 선택 매칭 개념을 앞에(내부는 우선순위순), 잔여 슬롯은 전체 풀
 *   상위로 채움 — 선택이 풀 밖(숙달·표본부족)이어도 Top5가 비지 않는다. 진단(P1~P3)은 항상 전체.
 *   비우면 전체(기존 동작).
 */

import { buildRatingView } from '../../utils/ratingHistory';

const MATH_TYPE_NAME = { AL: '대수', GE: '기하', AN: '해석', PS: '확률·통계' };
const MASTERED = 0.85;
const MID = 0.70;
const UNSTABLE_ST = 0.6; // 숙달인데 안정도 낮음(불안정 숙달) 기준

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const p90 = (a) => {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y);
  return s[clamp(Math.round(0.9 * (s.length - 1)), 0, s.length - 1)];
};
const bandOf = (acc) => (acc == null ? 'unlearned' : acc >= MASTERED ? 'mastered' : acc >= MID ? 'mid' : 'weak');
const buildDots = (m, mi, w) => [
  ...Array.from({ length: m }, () => 'mastered'),
  ...Array.from({ length: mi }, () => 'mid'),
  ...Array.from({ length: w }, () => 'weak'),
];

export function adaptReportForDraft(d, selectedConceptIds = [], ratingExams = [], ratingV2) {
  const concepts = d.concepts || [];
  const flat = d.flatAttempts || [];
  const conceptsById = d.conceptsById || {};
  const counts = d.meta.counts;

  // 처방 선택 우선 정렬용 (비우면 전체)
  const selSet = new Set((selectedConceptIds || []).map(Number));
  const hasSel = selSet.size > 0;

  // 엔진 정규화: direct=(r+V)/2V, related=h/p90(h), st=stabilityNorm. V는 buildReport와 동일 소스(단일화).
  const V = d.vScale || 10000;
  const hCeil = p90(concepts.filter((c) => c.hStatus != null).map((c) => c.hStatus)) || V;
  const direct = (c) => (c.rStatus == null ? null : clamp((c.rStatus + V) / (2 * V), 0, 1));
  const related = (c) => (c.hStatus == null ? null : clamp(c.hStatus / hCeil, 0, 1));
  const stOf = (c) => (c.stabilityNorm == null ? null : clamp(c.stabilityNorm, 0, 1));

  // 최장 연속 활동 주
  let streak = 0;
  let longestStreak = 0;
  (d.weeklySeries || []).forEach((w) => {
    if (w.active) { streak += 1; longestStreak = Math.max(longestStreak, streak); } else streak = 0;
  });

  // 누적 학습: cumulativeLearned → cumulative(차트 dataKey)
  const cumulativeGrowth = (d.cumulativeGrowth || []).map((x) => ({
    month: x.month, cumulative: x.cumulativeLearned ?? x.cumulative ?? 0,
  }));

  // 마지막 시험(가장 최근 created_at) 정답률·개념 수
  let lastExam = { accuracy: d.overallAcc ?? 0, concepts: 0 };
  if (flat.length) {
    const lastAt = flat.reduce((m, a) => (a.created_at > m ? a.created_at : m), flat[0].created_at);
    const sub = flat.filter((a) => a.created_at === lastAt);
    const cset = new Set();
    sub.forEach((a) => a.cids.forEach((cid) => cset.add(cid)));
    const correct = sub.filter((a) => a.correct).length;
    lastExam = { accuracy: sub.length ? correct / sub.length : 0, concepts: cset.size };
  }

  // 난이도별 정답률: 배열 [{level, accuracy, count}] (레벨 1~5)
  // 무표본(count 0) 레벨은 accuracy=null 유지 — 뷰가 '–'로 표시(ladderOf와 동일. 0 치환 시 무데이터가 최저 정답률 적색 0%로 오독됨).
  const difficultyBands = [1, 2, 3, 4, 5].map((level) => {
    const sub = flat.filter((a) => a.difficulty === level);
    const correct = sub.filter((a) => a.correct).length;
    return { level, accuracy: sub.length ? correct / sub.length : null, count: sub.length };
  });

  // 수학 영역(math_type)별 집계
  const byMath = {};
  concepts.forEach((c) => {
    const k = c.mathType || 'AL';
    const g = (byMath[k] || (byMath[k] = { key: k, learned: 0, mastered: 0, mid: 0, weak: 0, attempts: 0, correct: 0, mastConcepts: [] }));
    g.learned += 1;
    const band = bandOf(c.accuracy);
    if (band === 'mastered') { g.mastered += 1; g.mastConcepts.push(c); }
    else if (band === 'mid') g.mid += 1;
    else if (band === 'weak') g.weak += 1;
    g.attempts += c.attempts || 0;
    g.correct += c.correct || 0;
  });

  const typeSummary = Object.values(byMath).map((g) => ({
    key: g.key,
    name: MATH_TYPE_NAME[g.key] || g.key,
    accuracy: g.attempts ? g.correct / g.attempts : 0,
    n: g.attempts,
    learned: g.learned,
    mastered: g.mastered,
    weak: g.weak,
  }));

  const byType = {};
  let unstableMasteredCount = 0;
  Object.values(byMath).forEach((g) => {
    const unstable = g.mastConcepts.filter((c) => c.stabilityNorm != null && c.stabilityNorm < UNSTABLE_ST).length;
    unstableMasteredCount += unstable;
    byType[g.key] = {
      label: g.key,
      learned: g.learned,
      mastered: g.mastered,
      weak: g.weak,
      dots: buildDots(g.mastered, g.mid, g.weak),
      unstable,
    };
  });

  // 취약 공식(정밀 진단 카드)용 보강: ladder·direct/related/st·함께틀림(이름)·세션 추정
  // acc=null(해당 난이도 무표본)은 null 유지 — 뷰가 '–'로 표시(0 치환 시 무데이터가 최저 정답률 적색 0%로 오독됨)
  const ladderOf = (c) => (c.diffBreakdown || []).map((b) => ({
    level: b.d, accuracy: b.acc, count: (b.c || 0) + (b.w || 0),
  }));
  // 함께 틀림 목록도 개념 타입 배지를 달아야 해서 이름만이 아니라 타입을 함께 넘긴다(정밀 진단 카드 머리·처방 상세와 같은 배지)
  const partnersOf = (c) => (c.topPairPartners || [])
    .map((p) => conceptsById[p.cid])
    .filter((x) => x && x.name)
    .map((x) => ({ name: x.name, conceptType: x.conceptType || null }));
  const sessionsOf = (c) => Math.max(1, Math.ceil((c.unlockN || 0) / 3));
  const enrich = (c) => ({
    ...c,
    ladder: ladderOf(c),
    direct: direct(c),
    related: related(c),
    st: stOf(c),
    sessions: sessionsOf(c),
    topPairPartners: partnersOf(c),
    // def(LaTeX)·prereq는 신뢰 소스 없음 → 미표시(잘못된 LaTeX가 KaTeX를 크래시시키지 않도록)
    def: undefined,
    prereq: null,
  });

  // 처방: 선택 매스컨셉 우선 정렬(2026-07-04 — 종전 하드 필터 폐기). 선택 매칭 개념이 앞(내부는
  // 엔진 우선순위순), 나머지 풀이 뒤따라 Top5 잔여 슬롯을 채움. isSelected는 카드의 '선택' 칩 표시용.
  const enriched = (d.weakRanked || []).map((c) => {
    const e = enrich(c);
    if (hasSel && selSet.has(Number(c.cid))) e.isSelected = true;
    return e;
  });
  const weakRanked = hasSel
    ? [...enriched.filter((c) => c.isSelected), ...enriched.filter((c) => !c.isSelected)]
    : enriched;
  // 선택 요약(P4 부제용): 선택 N개 중 처방 풀(세이프가드 A∪B−C — buildReport.inRxPool 참조) 매칭 M개. 매칭 0이어도
  // Top5는 전체 풀로 채워지므로 빈 처방 안내는 풀 자체가 빌 때만 나온다. 선택 없으면 null.
  const rxSelection = hasSel ? { selected: selSet.size, matched: enriched.filter((c) => c.isSelected).length } : null;

  // 지속 오답: 처방 풀 중 진단 신뢰도(st) 높고 정답률 낮은 확정 취약 — 정오 반복 없이 계속 틀림.
  // (풀에 만성 취약 세이프가드로 acc 50% 초과 개념도 들어올 수 있어 acc≤0.5 조건을 명시 유지)
  // 우선순위와 별개 안전망 트랙 — 항상 전체 기준(계획 결정⑤), 선택 우선 정렬의 영향을 받지 않음.
  const persistentMiss = weakRanked
    .filter((c) => (c.st ?? 0) >= 0.6 && (c.accuracy ?? 1) <= 0.5)
    .sort((a, b) => (b.wrong || 0) - (a.wrong || 0));

  // 처방 풀(weakRanked)과 서로소 보장: 세이프가드 풀은 acc≥85%라도 10회 미만이면 편입될 수 있어
  // 숙달 밴드와 겹칠 수 있고, 겹치면 P3 매트릭스에 같은 공식 칩이 2번 그려진다(중복 React key).
  const rxPoolCids = new Set(weakRanked.map((c) => Number(c.cid)));
  const byBandMasteredTop = ((d.byBand && d.byBand.mastered) || [])
    .filter((c) => !rxPoolCids.has(Number(c.cid)))
    .sort((a, b) => (b.accuracy || 0) - (a.accuracy || 0))
    .slice(0, 8)
    .map((c) => ({ name: c.name, accuracy: c.accuracy, n: c.attempts, direct: direct(c), related: related(c), st: stOf(c) }));

  // ── 폼레이팅(학생별) ──────────────────────────────────────────────
  // v2는 저장된 확정 순서·점수·delta를 사용한다. 기존 저장본은 당시 값으로 표시한다.
  const rating = buildRatingView(ratingV2, ratingExams);

  // 정답률/난이도 '현재' = 가장 최근 시험(flatAttempts의 최대 created_at) subset
  let lastSub = [];
  if (flat.length) {
    const lastAt = flat.reduce((m, a) => (a.created_at > m ? a.created_at : m), flat[0].created_at);
    lastSub = flat.filter((a) => a.created_at === lastAt);
  }
  const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
  const meanDiff = (arr) => (arr.length ? arr.reduce((s, a) => s + (a.difficulty || 0), 0) / arr.length : 0);
  const cumA = flat.filter((a) => a.correct).length;
  const curA = lastSub.filter((a) => a.correct).length;
  // 문항 단위 전체 정답률 — P2 '정답률 전체/최근 시험/변화'의 모수 통일용(최근 시험이 문항 단위·미라벨 포함이므로
  // 공식 단위 overallAccuracy와 비교하면 변화 %p가 오도됨). 게이지·산식은 공식 단위 overallAccuracy 유지.
  const itemAccuracyAll = flat.length ? cumA / flat.length : (d.overallAcc ?? 0);

  const formRating = {
    ...rating,
    accuracy: {
      cur: { rate: pct(curA, lastSub.length), a: curA, b: lastSub.length },
      cum: { rate: pct(cumA, flat.length), a: cumA, b: flat.length },
    },
    difficulty: { cur: meanDiff(lastSub), cum: meanDiff(flat) }, // 문제 난도 1~5 평균
  };

  // 분석 깔때기(5단계, 단조 감소 보장): 전체 ⊇ 학습 ⊇ 보강 후보(전역 처방 풀 = 세이프가드 A∪B−C, buildReport.inRxPool) ⊇ 보강 순위(상위 5) ⊇ 바로 보강(상위 3).
  // 종전 '숙달·집중 관리' 단계는 배타 밴드라 취약 우세 학생에서 값이 증가(숙달 53 → 집중 관리 293)해 깔때기 서사가 깨졌음.
  // 상위 5 = P4 보강 1~5순위 카드·보강 상세 5행(top5) / 상위 3 = 보강 요약·단기 로드맵 3행(top3)과 정렬.
  // 「선택 우선」 표기는 화면에서 뺐다(2026-09-10 D2) — 정렬 자체(hasSel)는 그대로 살아 있다.
  const rxPoolAll = (d.weakRanked || []).length;
  const rxLen = weakRanked.length;
  const funnel = [
    { value: counts.totalConceptSpace, label: '분석 대상', sub: '전체 매스컨셉' },
    { value: counts.learnedCount, label: '학습', sub: `미학습 ${counts.unlearnedCount}` },
    { value: rxPoolAll, label: '보강 후보', sub: '정답률 낮음 · 풀이 3회 이상' },
    { value: Math.min(rxLen, 5), label: '보강 순위', sub: '상위 5' },
    { value: Math.min(rxLen, 3), label: '바로 보강', sub: '상위 3' },
  ];

  return {
    ...d,
    longestStreak,
    cumulativeGrowth,
    lastExam,
    overallAccuracy: d.overallAcc ?? 0,
    itemAccuracyAll,
    difficultyBands,
    typeSummary,
    byType,
    unstableMasteredCount,
    funnel,
    weakRanked,
    rxSelection,
    persistentMiss,
    byBandMasteredTop,
    formRating,
  };
}

export default adaptReportForDraft;
